import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { DesktopNav, MobileNav } from "./Navigation";
import { ACTIVITY_PAUSED, PAUSE_CTA_LABEL } from "../lib/constants";

function Brand({ onClick }) {
    return (
        <Link
            to="/"
            onClick={onClick}
            className="flex items-center min-w-0 cursor-pointer"
            data-testid="brand-link"
            aria-label="Données & Droit. Retour en haut de la page d'accueil"
        >
            <img
                src="/logo.png"
                alt="Données & Droit"
                className="h-10 sm:h-12 lg:h-16 w-auto select-none"
                draggable="false"
                data-testid="brand-logo"
            />
        </Link>
    );
}

function HeaderCta({ href }) {
    if (ACTIVITY_PAUSED) {
        return (
            <a
                href={href}
                className="hidden lg:inline-flex items-center gap-2 px-5 py-2.5 border border-slate-300 text-slate-600 bg-white/60 font-display text-[15px] font-medium tracking-tight rounded-full hover:border-slate-400 hover:text-[#0B132B] transition-colors duration-200 whitespace-nowrap"
                data-testid="header-contact-link"
                aria-label="Activité en pause. En savoir plus."
            >
                {PAUSE_CTA_LABEL}
            </a>
        );
    }
    return (
        <a
            href={href}
            className="hidden lg:inline-flex items-center gap-2 pl-6 pr-4 py-2.5 bg-[#0B132B] text-[#F9F9F8] font-display text-[17px] font-medium tracking-tight rounded-full hover:bg-[#1B4332] transition-all duration-200 whitespace-nowrap group"
            data-testid="header-contact-link"
        >
            Me contacter
            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-[#F9F9F8]/15 group-hover:bg-[#F9F9F8]/25 transition-colors">
                <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
            </span>
        </a>
    );
}

function MenuToggle({ open, onToggle }) {
    return (
        <button
            className="lg:hidden text-sm md:text-base font-medium text-[#0B132B] px-4 py-2 border border-[#0B132B] rounded-full whitespace-nowrap"
            onClick={onToggle}
            aria-label="Menu"
            data-testid="mobile-menu-toggle"
        >
            {open ? "Fermer" : "Menu"}
        </button>
    );
}

export function Header() {
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const onHome = location.pathname === "/";

    const navHref = (anchor) => (onHome ? anchor : `/${anchor}`);

    const handleBrandClick = (e) => {
        e.preventDefault();
        setOpen(false);
        if (onHome) {
            window.scrollTo({ top: 0, behavior: "smooth" });
        } else {
            navigate("/");
            window.scrollTo({ top: 0, behavior: "instant" });
        }
    };

    return (
        <header
            className="sticky top-0 z-40 bg-[#F9F9F8]/90 backdrop-blur-md border-b border-slate-200"
            data-testid="site-header"
        >
            <div className="mx-auto max-w-7xl px-5 md:px-10 lg:px-16">
                <div className="flex items-center justify-between h-[68px] md:h-20 lg:h-24 gap-4" data-testid="site-header-bar">
                    <Brand onClick={handleBrandClick} />
                    <DesktopNav navHref={navHref} />
                    <HeaderCta href={navHref("#contact")} />
                    <MenuToggle open={open} onToggle={() => setOpen(!open)} />
                </div>
                {open && <MobileNav navHref={navHref} onClose={() => setOpen(false)} />}
            </div>
        </header>
    );
}
